/**
 * Files the scan found and could not read because they are encrypted, per
 * store (release 8, C4; capability rows 2 and 78).
 *
 * A file that is encrypted or protected by a password is opened by nobody on
 * the scanner's side, so whatever personal data it holds is not in the
 * results. "No findings" on a store with encrypted files is not the same as a
 * clean store. This card says which stores have them and how many.
 *
 * Nothing new is collected: the agent has always counted the files it could
 * not read. From the first agent with the encrypted split it counts the
 * encrypted ones apart; older agents lump them in with "could not be opened",
 * and the card says so instead of showing a zero (an unknown is not a zero).
 *
 * A store is a laptop or a server scanner: the agent reports one summary per
 * run, not one per share, so this is per agent.
 */

import { useCallback, useEffect, useState } from 'react';
import { LockKeyhole, RefreshCw } from 'lucide-react';
import { ErrorState, LoadingState } from '@foctta/ui-shell';

import { Button } from '@/components/ui/button';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { getUnreadableStores, type UnreadableStore, type UnreadableStoresReport } from '@/lib/fleet-api';

import { BuFilterSelect } from './BuFilterSelect';
import { ago, fmtCount, plural } from './fleet-format';
import { useLatestGuard } from './use-latest';

/** "xlsx 3, docx 2" */
export function typesLine(types: Array<{ ext: string; count: number }>): string {
  return types.map((t) => `${t.ext} ${fmtCount(t.count)}`).join(', ');
}

/** The one sentence under the title. Never reassures when it cannot know. */
export function summaryLine(r: UnreadableStoresReport): string {
  const t = r.totals;
  if (t.storesReported === 0) {
    return 'No store has reported what its last scan read yet.';
  }
  if (t.storesSeparatingEncrypted === 0) {
    return `None of the ${plural(t.storesReported, 'store')} that reported can separate encrypted files yet. Update the agents to see them; until then they are counted under "could not be opened".`;
  }
  const parts: string[] = [];
  parts.push(
    t.encryptedFiles === 0
      ? `No encrypted files in the last scans of the ${plural(t.storesSeparatingEncrypted, 'store')} that can report them.`
      : `${plural(t.encryptedFiles, 'encrypted file')} in ${plural(t.storesWithEncrypted, 'store')}, not read.`,
  );
  const blind = t.storesReported - t.storesSeparatingEncrypted;
  if (blind > 0) {
    parts.push(`${plural(blind, 'other store')} cannot say (older agent).`);
  }
  return parts.join(' ');
}

function Row({ s }: { s: UnreadableStore }) {
  const where = s.businessUnitId === null ? 'Unassigned' : (s.businessUnitName ?? 'Not visible to you');
  return (
    <tr className="border-t border-border/30 align-top" data-testid="unreadable-row">
      <td className="py-2 pr-3">
        <div className="text-sm font-medium">{s.label}</div>
        <div className="text-xs text-muted-foreground">
          {s.kind === 'laptop' ? 'Laptop' : 'Server scanner'}
          {s.hostname && s.hostname !== s.label ? `, ${s.hostname}` : ''}
        </div>
      </td>
      <td className="py-2 pr-3 text-sm text-muted-foreground">{where}</td>
      <td className="py-2 pr-3 text-sm tabular-nums">
        {s.encryptedFiles === null ? (
          <span className="text-muted-foreground" title="This agent counts encrypted files with the files it could not open.">
            Not separated
          </span>
        ) : (
          fmtCount(s.encryptedFiles)
        )}
        {s.encryptedTypes.length > 0 && (
          <div className="text-xs text-muted-foreground">{typesLine(s.encryptedTypes)}</div>
        )}
        {s.encryptedExamples.length > 0 && (
          <details className="mt-1">
            <summary className="cursor-pointer text-xs text-primary hover:underline">
              Show {s.encryptedExamples.length} name{s.encryptedExamples.length === 1 ? '' : 's'}
            </summary>
            <ul className="mt-1 space-y-0.5 font-mono text-xs break-all text-foreground/80">
              {s.encryptedExamples.map((e) => (
                <li key={e}>{e}</li>
              ))}
              {s.encryptedFiles !== null && s.encryptedFiles > s.encryptedExamples.length && (
                <li className="font-sans text-muted-foreground">
                  and {fmtCount(s.encryptedFiles - s.encryptedExamples.length)} more
                </li>
              )}
            </ul>
          </details>
        )}
      </td>
      <td className="py-2 pr-3 text-sm tabular-nums text-muted-foreground">
        {s.couldNotOpenFiles === null ? '-' : fmtCount(s.couldNotOpenFiles)}
        {s.encryptedFiles === null && s.couldNotOpenFiles !== null && s.couldNotOpenFiles > 0 && (
          <div className="text-xs">may include encrypted files</div>
        )}
      </td>
      <td className="py-2 text-sm text-muted-foreground whitespace-nowrap">{ago(s.lastScanFinishedAt)}</td>
    </tr>
  );
}

export const PAGE_SIZE = 50;
export const SHOW_ALL_LIMIT = 1000;

export function UnreadableStoresCard({ bu: buProp }: { bu?: string } = {}) {
  const [report, setReport] = useState<UnreadableStoresReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bu, setBu] = useState<string>(buProp ?? '');
  const [showAll, setShowAll] = useState(false);
  const begin = useLatestGuard();

  useEffect(() => setBu(buProp ?? ''), [buProp]);

  const load = useCallback(async () => {
    const current = begin();
    setError(null);
    try {
      const r = await getUnreadableStores({ limit: showAll ? SHOW_ALL_LIMIT : PAGE_SIZE, ...(bu ? { bu } : {}) });
      if (current()) setReport(r);
    } catch (e) {
      if (current()) setError(e instanceof Error ? e.message : 'Could not load the unreadable-files report');
    }
  }, [bu, showAll, begin]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <GlassCard className="p-5 mb-6" data-testid="unreadable-stores-card">
      <CardHeader className="p-0 mb-3 flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <LockKeyhole className="h-4 w-4 text-muted-foreground" aria-hidden="true" /> Encrypted files not read: scanners and laptops
          </CardTitle>
          <CardDescription className="text-xs">
            A file that is encrypted or protected by a password is not opened, so any personal data in it is not in
            the results. These stores need a decision: get the files unlocked and rescanned, or accept that they
            are not covered.
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <BuFilterSelect id="unreadable-bu" value={bu} onChange={(v) => { setBu(v); setShowAll(false); }} />
          <Button size="sm" variant="glass" onClick={() => void load()}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </CardHeader>

      {error && <ErrorState title="Could not load the unreadable-files report" detail={error} onRetry={() => void load()} />}
      {!error && !report && <LoadingState caption="Reading the last scans..." />}

      {report && (
        <>
          <p className="text-sm mb-3" data-testid="unreadable-summary">
            {summaryLine(report)}
          </p>
          {report.items.length === 0 ? null : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs text-muted-foreground">
                    <th className="pb-1 pr-3 font-medium">Store</th>
                    <th className="pb-1 pr-3 font-medium">Business unit</th>
                    <th className="pb-1 pr-3 font-medium">Encrypted files</th>
                    <th className="pb-1 pr-3 font-medium">Could not be opened (other)</th>
                    <th className="pb-1 font-medium">Last scan</th>
                  </tr>
                </thead>
                <tbody>
                  {report.items.map((s) => (
                    <Row key={s.podRowId} s={s} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {report.truncated && (
            <p className="mt-2 text-xs text-muted-foreground flex flex-wrap items-center gap-2">
              Showing the {fmtCount(report.items.length)} stores with the most. The totals above count every store you can see.
              {!showAll && (
                <Button size="sm" variant="glass" onClick={() => setShowAll(true)}>
                  Show all
                </Button>
              )}
            </p>
          )}
          <p className="mt-3 text-xs text-muted-foreground">{report.basis}</p>
        </>
      )}
    </GlassCard>
  );
}
